import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";

import type { ManagedLocation } from "@thalamos/aspire-common";

import { ManagedLocationsContext } from "../../Contexts.js";
import { Typeahead } from "~/components/design-system/index.js";

/**
 * Picks one curated location of a given kind, storing its id on the form.
 *
 * Options come from the list already loaded for the session rather than a
 * search endpoint, and are filtered positively on type so that adding a kind of
 * location never widens what an existing picker offers.
 *
 * The current selection is resolved against every loaded location, not only
 * the ones this picker offers, so a link to a location of another kind is
 * still shown by name rather than reading as "not configured". A deleted
 * location is not in that list at all, so a link to one reads as blank.
 */
export function ManagedLocationPicker<T>({
  locationType,
  error,
  idField,
  values,
  setValues,
  label,
  disabled,
}: {
  locationType: ManagedLocation["type"];
  error: string | null;
  idField: keyof T;
  values: T;
  setValues: (values: T) => unknown;
  label: string;
  disabled?: boolean;
}) {
  const { managedLocations } = useContext(ManagedLocationsContext);

  const selectedId = (values[idField] as string | null) ?? null;
  const selectedName =
    managedLocations.find((location) => location.id === selectedId)?.name ?? "";

  const [query, setQuery] = useState<string>(selectedName);

  // Typing is what clears the stored id, so the field's own text has to win
  // while it is being edited: syncing on every change would wipe a part-typed
  // name the moment it stopped matching. Only an incoming selection pulls the
  // text back into line.
  useEffect(() => {
    if (selectedId !== null) {
      setQuery(selectedName);
    }
  }, [selectedId, selectedName]);

  const options = managedLocations
    .filter(
      (location) =>
        location.type === locationType && location.status === "active",
    )
    .map((location) => ({ label: location.name, value: location.id }));

  return (
    <Box sx={{ mt: 2 }}>
      <Typeahead
        disabled={disabled ?? false}
        inputValue={query}
        onInputChange={(value) => {
          setQuery(value);
          const match = options.find((option) => option.label === value);
          setValues({ ...values, [idField]: match ? match.value : null });
        }}
        options={options}
        name={String(idField)}
        label={label}
        errorMessage={error ?? undefined}
      />
    </Box>
  );
}
